// src/pages/admin/reports/ServiceChargeReport.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, DollarSign, CreditCard, ShoppingBag, RotateCcw } from 'lucide-react';
import BaseReportPage from '../../../components/reports/BaseReportPage';
import BranchService from '../../../services/branchService';
import AuthService from '../../../services/authService';
import { formatUTCDate } from '../../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Currency formatter helper
// ---------------------------------------------------------------------------
const fmtCurrency = (val) =>
  `Rs. ${parseFloat(val || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------
const SummaryCards = ({ summary }) => {
  const cards = [
    {
      id: 'total-orders',
      label: 'Total Orders',
      value: summary?.totalOrders ?? '—',
      icon: ShoppingBag,
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      isCount: true,
    },
    {
      id: 'net-service-charge',
      label: 'Net Service Charges',
      sublabel: 'After return deductions',
      value: summary ? fmtCurrency(summary.netTotalServiceCharge ?? summary.totalServiceCharge) : '—',
      icon: CreditCard,
      color: 'from-orange-500 to-orange-600',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
    },
    {
      id: 'net-revenue',
      label: 'Net Revenue',
      sublabel: 'After return deductions',
      value: summary ? fmtCurrency(summary.netTotalRevenue ?? summary.totalRevenue) : '—',
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      bg: 'bg-green-50',
      text: 'text-green-600',
    },
    {
      id: 'total-returns',
      label: 'Return Deductions',
      sublabel: summary?.ordersWithReturns ? `${summary.ordersWithReturns} order(s)` : null,
      value: summary ? fmtCurrency(summary.totalReturnAmount) : '—',
      icon: RotateCcw,
      color: 'from-red-400 to-red-500',
      bg: 'bg-red-50',
      text: 'text-red-600',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4"
          >
            {/* Icon circle */}
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-full ${card.bg} flex items-center justify-center`}
            >
              <Icon className={`w-6 h-6 ${card.text}`} />
            </div>

            {/* Text */}
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
                {card.label}
              </p>
              {card.sublabel && (
                <p className="text-xs text-gray-400 truncate">{card.sublabel}</p>
              )}
              <p className={`text-xl font-bold mt-0.5 ${card.text} truncate`}>
                {card.isCount ? card.value : card.value}
              </p>
            </div>

            {/* Accent stripe */}
            <div
              className={`ml-auto self-stretch w-1 rounded-full bg-gradient-to-b ${card.color} opacity-70`}
            />
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter component
// ---------------------------------------------------------------------------
const ServiceChargeFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = AuthService.getCurrentUser();
  const isManager = user?.role === 'manager';
  const userBranchId =
    user?.selectedBranch?._id || user?.branchId || user?.branch?._id;

  // Pre-set branch for manager on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      onFiltersChange({ ...filters, branch: userBranchId });
    }
  }, [isManager, userBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true);
        const res = await BranchService.getAllBranches();
        let data = [];
        if (res?.success) {
          data = res.data?.branches || res.data || [];
        }
        setBranches(data.filter((b) => b.isActive));
      } catch (err) {
        console.error('❌ ServiceChargeReport: Error loading branches:', err);
      } finally {
        setLoading(false);
      }
    };
    loadBranches();
  }, []);

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleClearFilters = () => {
    const cleared = { branch: isManager ? userBranchId : '' };
    if (onClear) onClear();
    onFiltersChange(cleared);
  };

  const hasActiveFilters = !isManager && filters?.branch;

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                {branches.find((b) => b._id === userBranchId)?.name ||
                  'My Branch'}
              </div>
            ) : (
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 pr-10 appearance-none"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {loading && !isManager && (
            <p className="text-xs text-gray-500 mt-1">Loading branches…</p>
          )}
        </div>

        {/* From Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Date
          </label>
          <input
            type="date"
            value={filters?.fromDate || ''}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>

        {/* To Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            To Date
          </label>
          <input
            type="date"
            value={filters?.toDate || ''}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Report page
// ---------------------------------------------------------------------------
const ServiceChargeReport = () => {
  const [summary, setSummary] = useState(null);

  const columns = useMemo(
    () => [
      {
        key: 'orderNumber',
        title: 'Order #',
        sortable: true,
      },
      {
        key: 'date',
        title: 'Date',
        sortable: true,
        type: 'date',
        render: (value) => {
          if (!value) return '-';
          return formatUTCDate(value, 'MMM dd, yyyy');
        },
      },
      {
        key: 'branchName',
        title: 'Branch',
        sortable: true,
      },
      {
        key: 'tableName',
        title: 'Table',
        sortable: false,
      },
      {
        key: 'customerName',
        title: 'Customer',
        sortable: true,
      },
      {
        key: 'subtotal',
        title: 'Subtotal',
        sortable: true,
        type: 'currency',
        render: (value) => fmtCurrency(value),
      },
      {
        key: 'serviceChargeRate',
        title: 'Rate (%)',
        sortable: true,
        render: (value) => (value ? `${parseFloat(value).toFixed(1)}%` : '—'),
      },
      {
        key: 'serviceChargeAmount',
        title: 'Gross S/C',
        sortable: true,
        type: 'currency',
        render: (value) => fmtCurrency(value),
      },
      {
        key: 'returnAmount',
        title: 'Return Deduction',
        sortable: true,
        type: 'currency',
        render: (value, row) => {
          if (!row?.hasReturn || !value) return <span className="text-gray-400">—</span>;
          return (
            <span className="text-red-600 font-medium">
              - {fmtCurrency(value)}
            </span>
          );
        },
      },
      {
        key: 'netServiceChargeAmount',
        title: 'Net S/C',
        sortable: true,
        type: 'currency',
        render: (value, row) => (
          <span className={row?.hasReturn ? 'text-orange-600 font-semibold' : ''}>
            {fmtCurrency(value)}
          </span>
        ),
      },
      {
        key: 'total',
        title: 'Gross Total',
        sortable: true,
        type: 'currency',
        render: (value) => fmtCurrency(value),
      },
      {
        key: 'netTotal',
        title: 'Net Total',
        sortable: true,
        type: 'currency',
        render: (value, row) => (
          <span className={row?.hasReturn ? 'text-green-700 font-semibold' : ''}>
            {fmtCurrency(value)}
          </span>
        ),
      },
      {
        key: 'paymentMethod',
        title: 'Payment',
        sortable: true,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      {/* Summary Cards — always visible, updates after data loads */}
      <SummaryCards summary={summary} />

      <BaseReportPage
        title="Service Charge Report"
        subtitle="Completed paid dine-in orders with service charge & return deductions"
        reportType="serviceCharge"
        columns={columns}
        enableFilters={true}
        customFilterComponent={ServiceChargeFilters}
        onDataLoaded={(result) => setSummary(result.summary || null)}
      />
    </div>
  );
};

export default ServiceChargeReport;
